import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { useRegion } from '../hooks/useRegion';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Clapperboard, ExternalLink, FileDown, Link2, Loader2, Plus, Save } from 'lucide-react';
import { toast } from 'sonner';
import TreatmentShotOutline from '../components/treatment/TreatmentShotOutline';
import { UnsavedChangesDialog, useUnsavedChangesGuard } from '../components/UnsavedChangesGuard';
import TreatmentShotEditor from '../components/treatment/TreatmentShotEditor';
import TreatmentSourcing from '../components/treatment/TreatmentSourcing';
import TreatmentPdfDialog from '../components/treatment/TreatmentPdfDialog';
import TreatmentShareDialog from '../components/treatment/TreatmentShareDialog';
import { useTreatmentHandlers } from '../components/treatment/useTreatmentHandlers';
import { assignSupplier, shotListError, shotNumbers, totalDurationSeconds } from '../lib/treatmentShots';
import { formatRunningTime } from '../lib/treatmentHelpers';
import { referencesError } from '../lib/treatmentReferences';
import { coverUrl, prepareShareImage } from '../lib/treatmentPdf';
import { isCurrentJob } from '../lib/jobStatus';
import { PageToolbar } from '../components/PageToolbar';
import { BackButton } from '../components/BackButton';
import { PhoneTitle } from '../components/PhoneHeader';

const NONE = '__none__';

// The fields the editor owns and sends whole on save.
const EDITABLE = ['title', 'document_label', 'director', 'client_id', 'client', 'job_id', 'approach', 'look', 'shots'];
const editable = (t) => (t ? Object.fromEntries(EDITABLE.map((k) => [k, t[k] ?? null])) : null);

export default function TreatmentDetailPage() {
  const { treatmentId } = useParams();
  const navigate = useNavigate();
  const { getTreatment, updateTreatment, getTreatmentShareImageSource, putTreatmentShareImage, jobs, fetchJobs, equipment, fetchEquipment, crewMembers, fetchCrewMembers } = useAppStore();
  const { hasProjectsPlan, isAdmin, isDemo, checkProjectsPlanAccess, projectsPlanChecked } = useAuthStore();
  const { formatCurrency } = useRegion();

  const [treatment, setTreatment] = useState(null);
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const tabsRef = useRef(null);
  const editorPaneRef = useRef(null);

  // A newly picked shot starts at its top, not wherever the last one was scrolled to.
  useEffect(() => {
    if (editorPaneRef.current) editorPaneRef.current.scrollTop = 0;
  }, [selectedId]);

  // Switch tab without moving the page. If the tab row is pinned at the top,
  // keep it there and show the new tab from its start; otherwise stay put.
  const switchView = (v) => {
    const el = tabsRef.current;
    const rowTop = el ? el.getBoundingClientRect().top + window.scrollY : 0;
    const pinned = el && window.scrollY > rowTop;
    setSearchParams(v === 'shots' ? {} : { view: v }, { replace: true });
    if (pinned) requestAnimationFrame(() => window.scrollTo({ top: rowTop }));
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'sourcing' ? 'sourcing' : 'shots';
  const handlers = useTreatmentHandlers(setTreatment);

  useEffect(() => {
    if (!projectsPlanChecked) checkProjectsPlanAccess();
  }, [projectsPlanChecked, checkProjectsPlanAccess]);

  useEffect(() => {
    fetchJobs();
    fetchEquipment();
    fetchCrewMembers();
  }, [fetchJobs, fetchEquipment, fetchCrewMembers]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const t = await getTreatment(treatmentId);
        if (cancelled) return;
        setTreatment(t);
        setSaved(editable(t));
        setSelectedId(t.shots?.[0]?.id || null);
      } catch (e) {
        if (!cancelled) toast.error('Shot list not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [treatmentId, getTreatment]);

  const dirty = useMemo(
    () => treatment && saved && JSON.stringify(editable(treatment)) !== JSON.stringify(saved),
    [treatment, saved],
  );

  const { blocker } = useUnsavedChangesGuard(dirty);

  // Keep the link-preview image on the treatment's first frame. Quietly, after
  // a save or once sharing is switched on - a preview without its picture is
  // not worth an error message.
  const syncShareImage = useCallback(async (t) => {
    const cover = t?.share_enabled ? coverUrl(t.shots) : null;
    if (!cover) return;
    try {
      if ((await getTreatmentShareImageSource(t.id)) === cover) return;
      const image = await prepareShareImage(cover);
      if (image) await putTreatmentShareImage(t.id, image, cover);
    } catch {
      // the frame could not be fetched, or it changed meanwhile: the next save tries again
    }
  }, [getTreatmentShareImageSource, putTreatmentShareImage]);

  // And once on opening a shared treatment, so one shared before the preview
  // image existed (or whose save could not make it) gets it without a save.
  const syncedOnOpen = useRef(null);
  useEffect(() => {
    if (!treatment || !saved || syncedOnOpen.current === treatment.id) return;
    syncedOnOpen.current = treatment.id;
    syncShareImage({ ...saved, id: treatment.id, share_enabled: treatment.share_enabled });
  }, [treatment, saved, syncShareImage]);

  /**
   * Save exactly `t` - the caller passes the treatment it means, so a save
   * straight after an edit never sends the state from before it.
   */
  const saveTreatment = useCallback(async (t, { silent = false } = {}) => {
    if (!t.title?.trim()) {
      toast.error('Title is required');
      return false;
    }
    const error = shotListError(t.shots || []) || referencesError(t.shots, shotNumbers(t.shots || []));
    if (error) {
      toast.error(error);
      return false;
    }
    setSaving(true);
    try {
      const updated = await updateTreatment(treatmentId, editable(t));
      setTreatment(updated);
      setSaved(editable(updated));
      if (!silent) toast.success('Shot list saved');
      syncShareImage(updated);
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save shot list');
      return false;
    } finally {
      setSaving(false);
    }
  }, [treatmentId, updateTreatment, syncShareImage]);

  const handleSave = () => saveTreatment(treatment);


  if (!hasProjectsPlan && !isAdmin && !isDemo) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto text-center py-20">
          <Clapperboard className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <h1 className="section-title mb-2">Shot lists</h1>
          <p className="text-muted-foreground">Shot lists are available on the Filmmaker plan.</p>
        </div>
      </Layout>
    );
  }

  if (loading || !treatment) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          {loading ? <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /> : <p className="text-muted-foreground">Shot list not found.</p>}
        </div>
      </Layout>
    );
  }

  const shots = treatment.shots || [];
  const numbers = shotNumbers(shots);
  const selected = shots.find((s) => s.id === selectedId) || null;
  const linkedJob = jobs.find((j) => j.id === treatment.job_id);

  // A setup joins its sequence; a shot goes after the one selected.
  const addSetup = (parentId) => setSelectedId(handlers.addShot(parentId));
  const addShot = () => setSelectedId(handlers.addShot(null, selectedId));

  const removeShot = (shotId) => {
    const shot = shots.find((s) => s.id === shotId);
    const setupCount = shots.filter((s) => s.parent_id === shotId).length;
    const what = setupCount ? `shot ${numbers[shotId]} and its ${setupCount} setup${setupCount > 1 ? 's' : ''}` : `shot ${numbers[shotId]}`;
    if (!window.confirm(`Delete ${what}${shot?.title ? ` (${shot.title})` : ''}?`)) return;
    const index = shots.findIndex((s) => s.id === shotId);
    const remaining = shots.filter((s) => s.id !== shotId && s.parent_id !== shotId);
    handlers.removeShot(shotId);
    setSelectedId(remaining[Math.max(0, Math.min(index - 1, remaining.length - 1))]?.id || null);
  };

  // Only live jobs are offered, plus whichever is already linked so it still shows.
  const jobOptions = jobs.filter((j) => isCurrentJob(j) || j.id === treatment.job_id);

  // Unsaved changes are caught by the guard, which asks before leaving.
  const goBack = () => navigate('/briefs?tab=treatments');

  return (
    <Layout>
      <div className="space-y-8 animate-in" data-testid="treatment-detail-page">
        {/* Header - the same shape as the job form's */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PhoneTitle title={treatment.title || 'Untitled shot list'} onBack={goBack} backLabel="Back to shot lists" />
          <div className="title-row gap-3 min-w-0 max-sm:hidden">
            <BackButton onClick={goBack} label="Back to shot lists" />
            <div className="min-w-0">
              <h1 className="record-title truncate">{treatment.title || 'Untitled shot list'}</h1>
            </div>
            <div className="hidden sm:block w-px h-6 bg-line shrink-0" />
            <p className="hidden sm:block text-muted-foreground truncate">
              {[treatment.client, `${shots.filter((s) => !s.parent_id).length} shots`, formatRunningTime(totalDurationSeconds(shots))].filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <p className="eyebrow">the whole piece</p>
            <CardTitle className="section-title flex items-center gap-2">
              <Clapperboard className="w-[18px] h-[18px]" /> Shot list
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="treatment-title">Title</Label>
                <Input
                  id="treatment-title"
                  value={treatment.title}
                  onChange={(e) => handlers.updateField('title', e.target.value)}
                  data-testid="treatment-title"
                />
              </div>
              {/* What the shared page and its PDF call this, in place of "Shot list" */}
              <div className="space-y-2">
                <Label htmlFor="treatment-label">Shown to the client as</Label>
                <Input
                  id="treatment-label"
                  value={treatment.document_label || ''}
                  placeholder="Shot list"
                  maxLength={60}
                  onChange={(e) => handlers.updateField('document_label', e.target.value)}
                  data-testid="treatment-document-label"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="treatment-director">Director</Label>
                <Input
                  id="treatment-director"
                  value={treatment.director || ''}
                  maxLength={120}
                  onChange={(e) => handlers.updateField('director', e.target.value)}
                  data-testid="treatment-director"
                />
              </div>
              <div className="space-y-2">
                <Label>Job</Label>
                <div className="flex gap-2">
                  <Select
                    value={treatment.job_id || NONE}
                    onValueChange={(v) => handlers.updateField('job_id', v === NONE ? null : v)}
                  >
                    <SelectTrigger aria-label="Linked job" data-testid="treatment-job-link">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not linked</SelectItem>
                      {jobOptions.map((j) => (
                        <SelectItem key={j.id} value={j.id}>
                          {[j.job_number, j.project_name].filter(Boolean).join(' · ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {linkedJob && (
                    <Button variant="outline" size="icon" asChild aria-label="Open the linked job" title="Open job">
                      <Link to={`/jobs/${linkedJob.id}`}><ExternalLink className="w-4 h-4" /></Link>
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="treatment-approach">Approach</Label>
                <Textarea
                  id="treatment-approach"
                  rows={3}
                  value={treatment.approach || ''}
                  onChange={(e) => handlers.updateField('approach', e.target.value)}
                  placeholder="The idea behind the whole piece."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="treatment-look">Look &amp; feel</Label>
                <Textarea
                  id="treatment-look"
                  rows={3}
                  value={treatment.look || ''}
                  onChange={(e) => handlers.updateField('look', e.target.value)}
                  placeholder="Grade, aspect ratio, pace, mood."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs ref={tabsRef} value={view} onValueChange={switchView} className="w-full space-y-6">
          {/* Tabs with the page's actions beside them, as the job page has its status */}
          {/* Sticky on wide screens; on a phone the app's menu button owns that corner */}
          <PageToolbar hairline={false} className="sm:flex-row sm:items-center sm:justify-between">
            <TabsList className="w-fit">
              <TabsTrigger value="shots" data-testid="treatment-view-shots">Shots</TabsTrigger>
              <TabsTrigger value="sourcing" data-testid="treatment-view-sourcing">Sourcing</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShareOpen(true)} data-testid="share-treatment" className={treatment.share_enabled ? 'text-moss' : ''}>
                <Link2 className="w-4 h-4" /> {treatment.share_enabled ? 'Shared' : 'Share'}
              </Button>
              <Button variant="outline" onClick={() => setPdfOpen(true)} data-testid="export-treatment-pdf">
                <FileDown className="w-4 h-4" /> Export PDF
              </Button>
              <Button onClick={handleSave} disabled={saving || !dirty} data-testid="save-treatment-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {dirty ? 'Save' : 'Saved'}
              </Button>
            </div>
          </PageToolbar>

          {/* At least a screen tall, so switching tabs never shortens the page under the reader */}
          <TabsContent value="shots" className="mt-0 lg:min-h-screen">
            {/* One panel: the shot being edited, and every shot beside it. Each half
                scrolls inside it. Sticky and never taller than the gap between the
                tab row (top-20) and the layout's bottom padding (2rem). */}
            {/* Sticks under the 80px toolbar and is exactly as tall as what's left above the
                page's 48px bottom padding, so scrolling to the end never slides it under the bar */}
            <Card className="flex flex-col lg:flex-row overflow-hidden lg:sticky lg:top-20 lg:h-[calc(100vh-8rem)]">
              <div className="lg:order-2 lg:w-[22rem] lg:shrink-0 border-b border-line lg:border-b-0 lg:border-l lg:flex lg:flex-col lg:min-h-0">
                <TreatmentShotOutline
                  shots={shots}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onMove={handlers.moveShot}
                  onAddShot={addShot}
                  onAddSetup={addSetup}
                  onDuplicate={(shotId) => setSelectedId(handlers.duplicateShot(shotId))}
                  onRemove={removeShot}
                />
              </div>
              <div ref={editorPaneRef} className="lg:order-1 flex-1 min-w-0 lg:overflow-y-auto scrollbar-thin">
                {selected ? (
                  <TreatmentShotEditor
                    key={selected.id}
                    shot={selected}
                    number={numbers[selected.id]}
                    equipment={equipment}
                    formatCurrency={formatCurrency}
                    handlers={handlers}
                    crewMembers={crewMembers}
                  />
                ) : (
                  <div className="py-12 text-center space-y-3">
                    <p className="text-muted-foreground">No shots yet.</p>
                    <Button onClick={addShot}>
                      <Plus className="w-4 h-4" /> Add the first shot
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="sourcing" className="mt-0 lg:min-h-screen">
            <TreatmentSourcing
              treatmentId={treatmentId}
              dirty={dirty}
              linkedJobId={treatment.job_id}
              onSave={handleSave}
              onAssign={(equipmentIds, supplierId) =>
                saveTreatment({ ...treatment, shots: assignSupplier(treatment.shots, equipmentIds, supplierId) }, { silent: true })
              }
            />
          </TabsContent>
        </Tabs>
      </div>
      <UnsavedChangesDialog blocker={blocker} />
      <TreatmentPdfDialog open={pdfOpen} onOpenChange={setPdfOpen} treatment={treatment} dirty={dirty} />
      <TreatmentShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        treatment={treatment}
        // Share fields aren't among the editor's saved fields, so this never marks the page dirty.
        onShareChange={(share) => {
          setTreatment((prev) => ({ ...prev, ...share }));
          // The server checks the image against the saved treatment, so use that
          if (share.share_enabled) syncShareImage({ ...saved, id: treatmentId, share_enabled: true });
        }}
      />
    </Layout>
  );
}
